<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import asaasConfigAPI from 'dashboard/api/asaasConfig';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const { t } = useI18n();

const isLoading = ref(true);
const isSaving = ref(false);

const form = ref({
  id: null,
  api_key: '',
  webhook_secret: '',
  environment: 'production',
  enabled: false,
});

const apiKeySet = ref(false);
const webhookSecretSet = ref(false);
const webhookUrl = ref('');

const apiKeyPlaceholder = computed(() =>
  apiKeySet.value
    ? t('INTEGRATION_SETTINGS.ASAAS.FORM.API_KEY_CONFIGURED')
    : t('INTEGRATION_SETTINGS.ASAAS.FORM.API_KEY_PLACEHOLDER')
);

const loadConfig = async () => {
  try {
    const { data } = await asaasConfigAPI.get();
    apiKeySet.value = Boolean(data.api_key_set);
    webhookSecretSet.value = Boolean(data.webhook_secret_set);
    webhookUrl.value = data.webhook_url || '';
    form.value = {
      id: data.id || null,
      api_key: '',
      webhook_secret: '',
      environment: data.environment || 'production',
      enabled: Boolean(data.enabled),
    };
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.ASAAS.API.LOAD_ERROR'));
  }
};

onMounted(async () => {
  isLoading.value = true;
  try {
    await loadConfig();
  } finally {
    isLoading.value = false;
  }
});

const buildPayload = () => {
  const payload = {
    environment: form.value.environment,
    enabled: form.value.enabled,
  };
  // Only send the api_key when the user typed a new one (write-only field).
  if (form.value.api_key) {
    payload.api_key = form.value.api_key;
  }
  if (form.value.webhook_secret)
    payload.webhook_secret = form.value.webhook_secret;
  return payload;
};

const saveConfig = async () => {
  isSaving.value = true;
  try {
    const payload = buildPayload();
    if (form.value.id) {
      await asaasConfigAPI.update(payload);
    } else {
      await asaasConfigAPI.create(payload);
    }
    useAlert(t('INTEGRATION_SETTINGS.ASAAS.API.SAVE_SUCCESS'));
    await loadConfig();
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.ASAAS.API.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('INTEGRATION_SETTINGS.ASAAS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.ASAAS.TITLE')"
        :description="$t('INTEGRATION_SETTINGS.ASAAS.DESCRIPTION')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-6">
        <div
          class="bg-n-alpha-2 border border-n-weak rounded-md p-4 text-sm text-n-slate-11"
        >
          {{ $t('INTEGRATION_SETTINGS.ASAAS.INTRO') }}
          <a
            href="https://www.asaas.com"
            target="_blank"
            rel="noopener noreferrer"
            class="text-n-brand underline"
          >
            {{ $t('INTEGRATION_SETTINGS.ASAAS.INTRO_LINK') }}
          </a>
        </div>

        <form
          class="flex flex-col gap-4 border border-n-weak rounded-md p-6 bg-n-alpha-3"
          @submit.prevent="saveConfig"
        >
          <!-- API key -->
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.ASAAS.FORM.API_KEY') }}
            </span>
            <input
              v-model="form.api_key"
              type="password"
              autocomplete="new-password"
              class="form-input"
              :placeholder="apiKeyPlaceholder"
            />
            <span class="text-xs text-n-slate-11">
              {{ $t('INTEGRATION_SETTINGS.ASAAS.FORM.API_KEY_HINT') }}
            </span>
          </label>

          <!-- Environment -->
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">{{
              t('PROPOSAL_PAYMENTS.WEBHOOK_SECRET')
            }}</span>
            <input
              v-model="form.webhook_secret"
              type="password"
              minlength="16"
              maxlength="128"
              autocomplete="new-password"
              class="rounded-lg border border-n-weak bg-n-background p-2 text-sm text-n-slate-12"
              :placeholder="
                webhookSecretSet ? t('PROPOSAL_PAYMENTS.SECRET_SET') : ''
              "
            />
            <span class="text-xs text-n-slate-11">{{
              t('PROPOSAL_PAYMENTS.WEBHOOK_HINT')
            }}</span>
          </label>
          <label v-if="webhookUrl" class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">{{
              t('PROPOSAL_PAYMENTS.WEBHOOK_URL')
            }}</span>
            <input
              :value="webhookUrl"
              readonly
              class="rounded-lg border border-n-weak bg-n-background p-2 text-sm text-n-slate-12"
            />
          </label>

          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.ASAAS.FORM.ENVIRONMENT') }}
            </span>
            <select v-model="form.environment" class="form-select">
              <option value="production">
                {{ $t('INTEGRATION_SETTINGS.ASAAS.FORM.ENV_PRODUCTION') }}
              </option>
              <option value="sandbox">
                {{ $t('INTEGRATION_SETTINGS.ASAAS.FORM.ENV_SANDBOX') }}
              </option>
            </select>
          </label>

          <!-- Enabled toggle -->
          <label class="flex items-center gap-2">
            <input v-model="form.enabled" type="checkbox" />
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.ASAAS.FORM.ENABLED') }}
            </span>
          </label>

          <div class="flex items-center gap-2 pt-2">
            <Button
              type="submit"
              icon="i-lucide-save"
              :label="$t('INTEGRATION_SETTINGS.ASAAS.FORM.SAVE')"
              :is-loading="isSaving"
            />
          </div>
        </form>
      </div>
    </template>
  </SettingsLayout>
</template>
